import * as React from 'react';
import PlacesAutocomplete, { Suggestion, Suggestion as PlacesAutocompleteAutoSuggestion } from 'react-places-autocomplete';
import cn from 'classnames';
import { useGooglePlacesApi } from './hooks/useGoogleMapsApi';
import { CSNGeographicPointFilter, CSNQueryParserResult, CSNBoundingBox, CSNServiceApi } from '@weddingspot/ws-api-client';

import { SearchIcon } from '@cvent/carina/components/Icon';
import AppLogger from '../../utils/logging';
const logger = AppLogger.createLogger();

const styles = require('./Autocomplete.module.scss');

interface Props {
    defaultAddress?: string;
    onPlaceSelected?: (address: string, geoFilter: CSNGeographicPointFilter, boundingBox: CSNBoundingBox) => void;
    onClear: () => void;
    onChangeActive: (isActive: boolean) => void;
    isActive: boolean; // onFocus + onBlur change state on parent component, then parent pass state back to this child
}

// The prop type is deliberately set to any due to typing issues with the react-places-autocomplete package
// The natural prop type should be React.InputHTMLAttributes<HTMLInputElement>, but the type exported for the
// getInputProps function of the library is not assignable to the type due to a misuse of generic types
const InputWithIcon = React.forwardRef((props: any, ref: React.RefObject<HTMLInputElement>) => {
    return (
        <div className={styles.input}>
            <input ref={ref} {...props} type='search' size={10} />
            <SearchIcon />
        </div>
    );
});

const inputCommonProps = {
    placeholder: 'Search by name or address',
};

const GooglePlacesAutocomplete = (props: Props) => {
    const { defaultAddress } = props;
    const mapsApiLoaded = useGooglePlacesApi();
    const [noResults, setNoResults] = React.useState(false);
    const [address, setAddress] = React.useState(props.defaultAddress || '');
    const inputRef: React.MutableRefObject<HTMLInputElement | null> = React.useRef(null);
    const [firstSuggestion, setFirstSuggestion] = React.useState<Suggestion | null>(null);

    React.useEffect(() => {
        setAddress(defaultAddress || '');
    }, [defaultAddress]);

    if (!mapsApiLoaded) {
        return <InputWithIcon {...inputCommonProps} disabled={true} />;
    }

    const selectHandler = (_address: string, _id: string, suggestion: PlacesAutocompleteAutoSuggestion) => {
        if (!_id && !suggestion) {
            selectHandler(firstSuggestion?.description as string, firstSuggestion?.placeId as string, firstSuggestion as Suggestion);
            return;
        }
        const getGeoFilter = (obj: CSNQueryParserResult): CSNGeographicPointFilter => {
            /*
            CSNQueryParser checking if a search term matches a pre-defined GeoJSON polygon stored in Elasticsearch.
            if the Google Suggestion geographic type matches one of the polygon types(
                * US states,
                * Canadian provinces,
                * the Caribbean
            ),
            CSNQueryParser provides 'POLYGON' filter
            */
            const polygon = (obj?.additionalFilters || []).find((i) => i.type === 'POLYGON');
            if (polygon) {
                return polygon;
            }
            return {
                ...obj.geographicPointFilter,
                maximum: 25,
                measurementUnit: 'MILES',
            };
        };

        const csnSuggestion = {
            queryString: suggestion.description,
            provider: 'google',
            types: suggestion.types,
            id: suggestion.placeId,
        };
        CSNServiceApi.queryParser({ autosuggestion: csnSuggestion })
            .then((result) => {
                setAddress(suggestion.description);
                inputRef.current && inputRef.current.blur();
                props.onPlaceSelected &&
                    props.onPlaceSelected(result.queryString, getGeoFilter(result), result.boundingBoxFilter.boundingBox);
            })
            .catch((error) => logger.warn(error));
    };

    const handleTabPressedOnSuggestion = (e: React.KeyboardEvent, suggestion?: PlacesAutocompleteAutoSuggestion) => {
        if (e.key === 'Tab' && suggestion) {
            selectHandler(suggestion.description, suggestion.placeId, suggestion);
        }
    };

    return (
        <PlacesAutocomplete
            onError={(status, clear) => {
                if (status === 'ZERO_RESULTS') {
                    setNoResults(true);
                }
                clear();
            }}
            value={address}
            onChange={(address) => {
                setNoResults(false);
                setAddress(address);
                if (!address) {
                    props.onClear();
                }
            }}
            // @ts-ignore
            // there is a mistake on PlacesAutocomplete interface
            // onSelect function actually have three arguments (address, placeID, suggestion)
            // not only (address, placeID)
            onSelect={selectHandler}
        >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
                const inputProps = getInputProps({
                    ...inputCommonProps,
                    onFocus: () => props.onChangeActive(true),
                    onClear: () => setAddress(''),
                });
                inputProps.onBlur = () => props.onChangeActive(!props.isActive);
                const defaultOnKeyDown = inputProps.onKeyDown;
                inputProps.onKeyDown = (e: React.KeyboardEvent) => {
                    defaultOnKeyDown(e);
                    const activeSuggestion = suggestions.find((item) => item.active);
                    handleTabPressedOnSuggestion(e, activeSuggestion);
                };

                setFirstSuggestion(suggestions[0] || null);
                return (
                    <div className={cn({ [styles.isActive]: props.isActive })}>
                        <InputWithIcon ref={inputRef} {...inputProps} />
                        {(noResults || (suggestions.length > 0 && props.isActive)) && (
                            <div className={styles.container}>
                                {noResults && <div className={styles.searchItem}>&#128683; No results found</div>}
                                {!noResults && loading && <div className={styles.searchItem}>&#9203; Loading...</div>}
                                {!noResults &&
                                    !loading &&
                                    suggestions.length > 0 &&
                                    suggestions.map((suggestion, idx) => {
                                        const { description, active } = suggestion;
                                        const addressStart = description.toLowerCase().indexOf(address.toLowerCase());
                                        const highLightDescription =
                                            addressStart === -1 ? (
                                                <span>{description}</span>
                                            ) : (
                                                <span>
                                                    {description.substring(0, addressStart)}
                                                    <b>{description.substring(addressStart, addressStart + address.length)}</b>
                                                    {description.substring(addressStart + address.length)}
                                                </span>
                                            );
                                        return (
                                            // tslint:disable-next-line: jsx-key getSuggestionItemProps return key
                                            <div
                                                {...getSuggestionItemProps(suggestion, {
                                                    className: cn({ [styles.active]: active }, styles.searchItem),
                                                })}
                                                key={suggestion.placeId}
                                            >
                                                {highLightDescription}
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>
                );
            }}
        </PlacesAutocomplete>
    );
};

export default GooglePlacesAutocomplete;
